import { z } from 'zod';
import { Id, IsoDate, IsoDateTime } from './common';
import { ConsentPath, ModuleVisibility, Nation, Role, Stage, SubscriptionStatus } from './enums';
import type { Permissions } from './permissions';

// ---------------------------------------------------------------- auth (spec §9.1, ADR 0007)

export const MagicLinkRequest = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  /** Optional path to return to after sign-in (must start with '/'). */
  redirectTo: z.string().startsWith('/').max(500).optional(),
});
export type MagicLinkRequest = z.infer<typeof MagicLinkRequest>;

export const MagicLinkVerifyRequest = z.object({ token: z.string().min(20).max(200) });
export type MagicLinkVerifyRequest = z.infer<typeof MagicLinkVerifyRequest>;

/** Strips the spaces/dashes people type or paste in a sign-in code ("123 456", "123-456" → "123456"). */
export const normaliseSignInCode = (raw: string) => raw.replace(/[\s-]/g, '');

/** The 6-digit sign-in code emailed with the magic link (typed instead of opening the link). */
export const SignInCode = z
  .string()
  .max(20)
  .transform(normaliseSignInCode)
  .pipe(z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from the email'));

/** POST /api/auth/magic-link/code — sign in with the code from the newest sign-in email (ADR 0007). */
export const MagicLinkCodeRequest = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  code: SignInCode,
});
export type MagicLinkCodeRequest = z.input<typeof MagicLinkCodeRequest>;

/** Returned by every sign-in completion endpoint. When `mfaRequired`, call POST /api/auth/totp/challenge. */
export const SignInResponse = z.object({
  mfaRequired: z.boolean(),
  redirectTo: z.string().nullable(),
});
export type SignInResponse = z.infer<typeof SignInResponse>;

export const TotpCodeRequest = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
export type TotpCodeRequest = z.infer<typeof TotpCodeRequest>;

export const TotpSetupResponse = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  qrSvg: z.string(),
});
export type TotpSetupResponse = z.infer<typeof TotpSetupResponse>;

export const AuthConfig = z.object({
  demoEnabled: z.boolean(),
  passkeysEnabled: z.boolean(),
  rpId: z.string(),
});
export type AuthConfig = z.infer<typeof AuthConfig>;

export const Passkey = z.object({
  id: Id,
  name: z.string(),
  createdAt: IsoDateTime,
  lastUsedAt: IsoDateTime.nullable(),
});
export type Passkey = z.infer<typeof Passkey>;

export const PasskeyRegisterVerifyRequest = z.object({
  name: z.string().trim().min(1).max(80).default('Passkey'),
  // RegistrationResponseJSON from @simplewebauthn/browser
  response: z.record(z.string(), z.unknown()),
});
export type PasskeyRegisterVerifyRequest = z.infer<typeof PasskeyRegisterVerifyRequest>;

export const PasskeyLoginOptionsRequest = z.object({
  /** Ignored (SR-35: options are always for discoverable credentials); accepted for older clients. */
  email: z.string().trim().toLowerCase().email().optional(),
});
export const PasskeyLoginVerifyRequest = z.object({
  challengeId: z.string(),
  // AuthenticationResponseJSON from @simplewebauthn/browser
  response: z.record(z.string(), z.unknown()),
});
export type PasskeyLoginVerifyRequest = z.infer<typeof PasskeyLoginVerifyRequest>;

export const SessionInfo = z.object({
  id: Id,
  current: z.boolean(),
  userAgent: z.string().nullable(),
  /** Coarse device label, e.g. "Chrome on Windows". */
  device: z.string(),
  createdAt: IsoDateTime,
  lastSeenAt: IsoDateTime,
  expiresAt: IsoDateTime,
});
export type SessionInfo = z.infer<typeof SessionInfo>;

// ---------------------------------------------------------------- /api/me

export const MeUser = z.object({
  id: Id,
  email: z.string(),
  name: z.string().nullable(),
  mfaEnabled: z.boolean(),
  hasPasskeys: z.boolean(),
  isPlatformAdmin: z.boolean(),
  lastLoginAt: IsoDateTime.nullable(),
});
export type MeUser = z.infer<typeof MeUser>;

export const WorkspaceSummary = z.object({
  id: Id,
  name: z.string(),
  role: Role,
  stage: Stage,
  nation: Nation,
  principalName: z.string().nullable(),
  subscriptionStatus: SubscriptionStatus,
  readOnly: z.boolean(),
});
export type WorkspaceSummary = z.infer<typeof WorkspaceSummary>;

export const MeResponse = z.object({
  user: MeUser,
  workspaces: z.array(WorkspaceSummary),
  csrfToken: z.string(),
});
export type MeResponse = z.infer<typeof MeResponse>;

export const UpdateMeRequest = z.object({ name: z.string().trim().min(1).max(120) });
export type UpdateMeRequest = z.infer<typeof UpdateMeRequest>;

// ---------------------------------------------------------------- onboarding / workspace creation (FR-1.1, FR-1.2)

export const OnboardingAnswers = z.object({
  /** Is the Principal making or planning gifts? */
  makingGifts: z.boolean(),
  /** Is the Principal receiving or about to need care? */
  needsCare: z.boolean(),
  /** Do you hold an LPA or deputyship? */
  holdsLpaOrDeputyship: z.boolean(),
});
export type OnboardingAnswers = z.infer<typeof OnboardingAnswers>;

export const CreateWorkspaceRequest = z.object({
  principalName: z.string().trim().min(1, 'Enter a name').max(120),
  nation: Nation,
  answers: OnboardingAnswers,
  consentPath: ConsentPath,
  /** Optional explicit workspace name; defaults to "<surname> family". */
  workspaceName: z.string().trim().min(1).max(120).optional(),
});
export type CreateWorkspaceRequest = z.infer<typeof CreateWorkspaceRequest>;

/** Module keys that can be switched on for a workspace (drives navigation and Home cards). */
export const EnabledModules = z.object({
  gifts: z.boolean(),
  money: z.boolean(),
  care: z.boolean(),
  health: z.boolean(),
  attorney: z.boolean(),
  documents: z.boolean(),
});
export type EnabledModules = z.infer<typeof EnabledModules>;

/**
 * FR-1.2: derive stage + enabled modules from the three onboarding answers.
 * Stage: care if needsCare, else gifting. Documents and money are always on.
 */
export function deriveFromAnswers(a: OnboardingAnswers): { stage: Stage; enabledModules: EnabledModules } {
  const stage: Stage = a.needsCare ? 'care' : 'gifting';
  return {
    stage,
    enabledModules: {
      documents: true,
      money: true,
      gifts: a.makingGifts || !a.needsCare,
      care: a.needsCare,
      health: a.needsCare,
      attorney: a.holdsLpaOrDeputyship,
    },
  };
}

export const SubscriptionInfo = z.object({
  status: SubscriptionStatus,
  plan: z.string().nullable(),
  trialEndsAt: IsoDateTime.nullable(),
  currentPeriodEnd: IsoDateTime.nullable(),
  /** True when new entries/exports are blocked (FR-13.2). */
  readOnly: z.boolean(),
  daysLeftInTrial: z.number().int().nullable(),
});
export type SubscriptionInfo = z.infer<typeof SubscriptionInfo>;

export const PrincipalBrief = z.object({
  id: Id,
  fullName: z.string(),
  nation: Nation,
  stage: Stage,
  dateOfDeath: IsoDate.nullable(),
});
export type PrincipalBrief = z.infer<typeof PrincipalBrief>;

export const WorkspaceDetail = z.object({
  id: Id,
  name: z.string(),
  nation: Nation,
  stage: Stage,
  ruleSetVersion: z.string().nullable(),
  enabledModules: EnabledModules,
  createdAt: IsoDateTime,
  subscription: SubscriptionInfo,
  /** Principal has a date of death → archive mode (FR-10.1). */
  archive: z.boolean(),
  principal: PrincipalBrief.nullable(),
  membership: z.object({
    role: Role,
    moduleVisibility: ModuleVisibility,
    /** R-3.2: attorney whose registered LPA / deputyship has not been linked yet (restricted rights). */
    lpaPending: z.boolean().optional(),
  }),
  permissions: z.custom<Permissions>(),
  consentRecorded: z.boolean(),
});
export type WorkspaceDetail = z.infer<typeof WorkspaceDetail>;

export const CreateWorkspaceResponse = z.object({ workspace: WorkspaceDetail });
export type CreateWorkspaceResponse = z.infer<typeof CreateWorkspaceResponse>;

// ---------------------------------------------------------------- push

export const PushSubscriptionRequest = z.object({
  endpoint: z.string().url(),
  keys: z.object({ p256dh: z.string(), auth: z.string() }),
});
export type PushSubscriptionRequest = z.infer<typeof PushSubscriptionRequest>;

// ---------------------------------------------------------------- rule sets (RE-1)

/**
 * Ed25519 signing contexts (security review SR-34). The one server key signs both rule sets (admin-chosen
 * payloads) and deletion certificates, so every signed message is prefixed with its purpose and a signature made
 * for one can never be passed off as the other. The signature is over the UTF-8 bytes of `context + payload`.
 * Verifiers must also accept a signature over the bare payload (signatures issued before 15 Oct 2026).
 */
export const SIGNING_CONTEXT_RULESET = 'seventide.ruleset.v1\n';
export const SIGNING_CONTEXT_DELETION_CERT = 'seventide.deletion-cert.v1\n';
export type SigningContext = typeof SIGNING_CONTEXT_RULESET | typeof SIGNING_CONTEXT_DELETION_CERT;

export const RuleSetResponse = z.object({
  version: z.string(),
  nation: Nation,
  taxYear: z.string(),
  effectiveFrom: IsoDate,
  verifiedOn: IsoDate.nullable(),
  source: z.enum(['published', 'bundled']),
  /** The rule-set JSON exactly as signed. */
  payload: z.string(),
  /** base64 Ed25519 signature over SIGNING_CONTEXT_RULESET + `payload` (UTF-8); older sets: `payload` alone. */
  signature: z.string(),
  publicKey: z.string(),
});
export type RuleSetResponse = z.infer<typeof RuleSetResponse>;

export const PasskeyLoginOptionsResponse = z.object({
  challengeId: z.string(),
  /** PublicKeyCredentialRequestOptionsJSON — pass to startAuthentication({ optionsJSON }). */
  options: z.record(z.string(), z.unknown()),
});
export type PasskeyLoginOptionsResponse = z.infer<typeof PasskeyLoginOptionsResponse>;

export const PasskeyRegisterOptionsResponse = z.object({
  /** PublicKeyCredentialCreationOptionsJSON — pass to startRegistration({ optionsJSON }). */
  options: z.record(z.string(), z.unknown()),
});
export type PasskeyRegisterOptionsResponse = z.infer<typeof PasskeyRegisterOptionsResponse>;
